import { Booking, Trip } from '../types';
import {
  compareGregorianByHijriCalendar,
  hijriSortKeyFromGregorian,
} from '../components/Forms/hijriDate';
import { toGregorianDateOnly } from './dateDisplay';

export const isFromHail = (city?: string): boolean => {
  if (!city) return false;
  const n = city.trim();
  return n === 'حائل' || n.includes('حائل');
};

const normalizeDepartureDate = (value: unknown): string =>
  value == null || value === '' ? '' : toGregorianDateOnly(value) || '';

const resolveHailRouteDateFrom = (
  routes: { from?: string; dateFrom?: string }[] | undefined
): string => {
  if (!routes?.length) return '';
  const hailRoute = routes.find((r) => isFromHail(r.from));
  if (hailRoute?.dateFrom) return hailRoute.dateFrom;
  const firstWithDate = routes.find((r) => r.dateFrom);
  return firstWithDate?.dateFrom || '';
};

/** هل يمثل التاريخان نفس يوم المغادرة */
export const departureDatesMatch = (a: string, b: string): boolean => {
  if (!a || !b) return false;

  const ga = toGregorianDateOnly(a);
  const gb = toGregorianDateOnly(b);
  if (ga && gb && ga === gb) return true;

  const hijriA = hijriSortKeyFromGregorian(a);
  const hijriB = hijriSortKeyFromGregorian(b);
  if (
    hijriA !== Number.MAX_SAFE_INTEGER &&
    hijriB !== Number.MAX_SAFE_INTEGER &&
    hijriA === hijriB
  ) {
    return true;
  }

  if (ga && hijriB !== Number.MAX_SAFE_INTEGER && hijriSortKeyFromGregorian(ga) === hijriB) {
    return true;
  }
  if (gb && hijriA !== Number.MAX_SAFE_INTEGER && hijriSortKeyFromGregorian(gb) === hijriA) {
    return true;
  }

  return a.trim() === b.trim();
};

/** تاريخ مغادرة مسار حائل YYYY-MM-DD ميلادي موحّد */
export const getTripHailDepartureGregorian = (trip: Trip): string => {
  const fromRoutes = normalizeDepartureDate(resolveHailRouteDateFrom(trip.routes));
  if (fromRoutes) return fromRoutes;

  if (trip.route && isFromHail(trip.route.from)) {
    const fromLegacy = normalizeDepartureDate(trip.route.departureTime);
    if (fromLegacy) return fromLegacy;
  }

  return normalizeDepartureDate(trip.date);
};

export const tripDepartsFromHail = (trip: Trip): boolean => {
  if (trip.routes?.length) {
    if (trip.routes.some((r) => isFromHail(r.from))) return true;
    const first = trip.routes[0];
    return !!first?.dateFrom && (!first.from || isFromHail(first.from));
  }
  if (trip.route?.from) return isFromHail(trip.route.from);
  return !!getTripHailDepartureGregorian(trip);
};

/** تاريخ مغادرة الحجز YYYY-MM-DD ميلادي موحّد */
export const getBookingHailDepartureGregorian = (booking: Booking): string => {
  const fromRoutes = normalizeDepartureDate(resolveHailRouteDateFrom(booking.routes));
  if (fromRoutes) return fromRoutes;

  if (booking.route?.departureDate) {
    const fromLegacy = normalizeDepartureDate(booking.route.departureDate);
    if (fromLegacy) return fromLegacy;
  }

  if (booking.assignedTrip && typeof booking.assignedTrip === 'object') {
    return getTripHailDepartureGregorian(booking.assignedTrip);
  }

  return '';
};

export const compareTripsByHailDepartureOldestFirst = (a: Trip, b: Trip): number => {
  const dateA = getTripHailDepartureGregorian(a);
  const dateB = getTripHailDepartureGregorian(b);
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return compareGregorianByHijriCalendar(dateB, dateA);
};

/** مفتاح موحّد لمقارنة وتجميع تواريخ المغادرة */
export const getDepartureDateCompareKey = (value: string): string =>
  toGregorianDateOnly(value) || value.trim();

/** هل تاريخ مغادرة الحجز يطابق تاريخ الفلتر */
export const bookingMatchesDepartureDate = (
  booking: Booking,
  filterDate: string
): boolean => {
  const filter = filterDate.trim();
  if (!filter) return true;

  const departure = getBookingHailDepartureGregorian(booking);
  return Boolean(departure && departureDatesMatch(departure, filter));
};

export const compareBookingsByHailDepartureOldestFirst = (
  a: Booking,
  b: Booking
): number => {
  const dateA = getBookingHailDepartureGregorian(a);
  const dateB = getBookingHailDepartureGregorian(b);
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return compareGregorianByHijriCalendar(dateB, dateA);
};
